/**
 * Region Detection Service
 *
 * Handles region detection API calls and AsyncStorage persistence.
 * Detects user's region (EU/UK/US) before signup and stores for later use.
 *
 * Related Spec: uk-us-expansion
 * Task: Task 10 - RegionDetectionService
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import { apiService } from './apiService.js';
import logger from '../utils/logger.js';

// AsyncStorage keys (namespaced with @doocars_ prefix)
const STORAGE_KEY_REGION = '@doocars_detected_region';
const STORAGE_KEY_COUNTRY = '@doocars_detected_country';

// Detection timeout (3 seconds to allow for network latency)
const DETECTION_TIMEOUT_MS = 3000;

// Default fallback region
const DEFAULT_REGION = 'UK';

/**
 * Detects user's region and stores it in AsyncStorage
 *
 * Calls the detect-region Edge Function with a 3-second timeout,
 * stores the result, and returns the detected region and country code.
 * Defaults to 'EU' on any error and logs failures to Sentry.
 *
 * @returns {Promise<{region: string, country_code: string|null, error: string|null}>}
 */
export async function detectAndStoreRegion() {
  // Dev-only region override (set EXPO_PUBLIC_DEV_REGION_OVERRIDE=UK in .env.development)
  const devOverride = process.env.EXPO_PUBLIC_DEV_REGION_OVERRIDE;
  if (__DEV__ && devOverride) {
    const region = devOverride.toUpperCase();
    logger.log(`[RegionDetection] Dev override active — using region: ${region}`);
    await AsyncStorage.multiSet([
      [STORAGE_KEY_REGION, region],
      [STORAGE_KEY_COUNTRY, region === 'UK' ? 'GB' : ''],
    ]);
    return { region, country_code: region === 'UK' ? 'GB' : null, error: null };
  }

  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DETECTION_TIMEOUT_MS);

    logger.log('[RegionDetection] Starting region detection via Cloudflare...');

    // Call the detect-region Edge Function via Cloudflare Worker
    // This ensures we get request.cf.country for reliable geo detection
    const functionData = await apiService.invokeEdgeFunction('detect-region', {
      method: 'GET',
      skipAuth: true,
      signal: controller.signal,
    });

    // Clear timeout if request completed
    clearTimeout(timeoutId);

    // Extract region and country_code from response
    const { region, country_code, error: detectionError } = functionData || {};

    if (detectionError) {
      // Detection failed but returned fallback
      logger.warn('[RegionDetection] Detection failed, using fallback:', detectionError);

      // Log to Sentry for monitoring
      Sentry.captureMessage('Region detection failed with fallback', {
        level: 'warning',
        extra: {
          error: detectionError,
          fallback_region: region || DEFAULT_REGION,
          timestamp: new Date().toISOString(),
        },
      });
    }

    // Determine final region (use returned region or fallback)
    const finalRegion = region || DEFAULT_REGION;
    const finalCountryCode = country_code || null;

    // Store in AsyncStorage
    await AsyncStorage.multiSet([
      [STORAGE_KEY_REGION, finalRegion],
      [STORAGE_KEY_COUNTRY, finalCountryCode || ''],
    ]);

    logger.log(`[RegionDetection] Successfully detected and stored - Region: ${finalRegion}, Country: ${finalCountryCode}`);

    return {
      region: finalRegion,
      country_code: finalCountryCode,
      error: detectionError || null,
    };
  } catch (error) {
    // Handle all errors (timeout, network, etc.)
    const isTimeout = error.name === 'AbortError';
    const errorMessage = isTimeout ? 'Region detection timeout' : error.message;

    logger.error('[RegionDetection] Detection failed:', errorMessage);

    // Only report non-timeout errors to Sentry; timeouts are expected on slow mobile connections
    if (!isTimeout) {
      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          service: 'region_detection',
          error_type: 'network_error',
        },
        extra: {
          error_message: errorMessage,
          timestamp: new Date().toISOString(),
        },
      });
    }

    // Store fallback region in AsyncStorage
    try {
      await AsyncStorage.multiSet([
        [STORAGE_KEY_REGION, DEFAULT_REGION],
        [STORAGE_KEY_COUNTRY, ''],
      ]);
    } catch (storageError) {
      logger.error('[RegionDetection] Failed to store fallback region:', storageError);
    }

    // Always return fallback (never throw to avoid blocking app)
    return {
      region: DEFAULT_REGION,
      country_code: null,
      error: errorMessage,
    };
  }
}

/**
 * Retrieves stored region and country code from AsyncStorage
 *
 * @returns {Promise<{region: string|null, country_code: string|null}>}
 */
export async function getStoredRegion() {
  try {
    const [[, region], [, countryCode]] = await AsyncStorage.multiGet([
      STORAGE_KEY_REGION,
      STORAGE_KEY_COUNTRY,
    ]);

    return {
      region: region || null,
      country_code: countryCode || null,
    };
  } catch (error) {
    logger.error('[RegionDetection] Failed to retrieve stored region:', error);
    Sentry.captureException(error, {
      level: 'warning',
      tags: { service: 'region_detection', operation: 'get_stored' },
    });

    return {
      region: null,
      country_code: null,
    };
  }
}

/**
 * Clears stored region data from AsyncStorage
 *
 * Useful for testing or resetting region detection.
 *
 * @returns {Promise<void>}
 */
export async function clearStoredRegion() {
  try {
    await AsyncStorage.multiRemove([STORAGE_KEY_REGION, STORAGE_KEY_COUNTRY]);
    logger.log('[RegionDetection] Cleared stored region data');
  } catch (error) {
    logger.error('[RegionDetection] Failed to clear stored region:', error);
    Sentry.captureException(error, {
      level: 'warning',
      tags: { service: 'region_detection', operation: 'clear_stored' },
    });
  }
}

/**
 * Gets the default region (EU)
 *
 * @returns {string} Default region
 */
export function getDefaultRegion() {
  return DEFAULT_REGION;
}
